const pdpHumidityTogglePlaybackRequests = new WeakMap();

const playPdpHumidityToggleVideo = (video) => {
  if (!video) return Promise.resolve(false);

  const pendingRequest = pdpHumidityTogglePlaybackRequests.get(video);
  if (pendingRequest) return pendingRequest;

  const playRequest = video.play();
  if (!playRequest || typeof playRequest.then !== 'function') {
    return Promise.resolve(true);
  }

  const handledRequest = playRequest
    .then(() => true)
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('PDP humidity toggle video play failed:', error);
      }

      return false;
    })
    .finally(() => {
      if (pdpHumidityTogglePlaybackRequests.get(video) === handledRequest) {
        pdpHumidityTogglePlaybackRequests.delete(video);
      }
    });

  pdpHumidityTogglePlaybackRequests.set(video, handledRequest);
  return handledRequest;
};

const loadPdpHumidityToggleVideo = (video) => {
  if (!video || video.dataset.videoLoaded === 'true') return;

  const sources = video.querySelectorAll('source[data-src]');

  sources.forEach((source) => {
    source.src = source.dataset.src;

    if (source.dataset.type) {
      source.type = source.dataset.type;
    }
  });

  video.dataset.videoLoaded = 'true';
  video.load();
};

class PdpHumidityToggle {
  constructor(container) {
    this.container = container;
    this.toggleButtons = Array.from(this.container.querySelectorAll('[data-toggle-button]'));
    this.videoPanels = Array.from(this.container.querySelectorAll('[data-video-panel]'));
    this.videoElements = Array.from(this.container.querySelectorAll('[data-video-element]'));
    this.activeType = 'rises';
    this.isInView = false;
    this.handleIntersection = this.handleIntersection.bind(this);

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupIntersectionObserver();
    this.activate(this.activeType, { force: true });
  }

  bindEvents() {
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activate(button.dataset.toggleTarget);
      });
    });
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(this.handleIntersection);
      },
      {
        threshold: 0.25,
        rootMargin: '300px 0px'
      }
    );

    this.observer.observe(this.container);
  }

  handleIntersection(entry) {
    if (entry.isIntersecting) {
      this.isInView = true;
      this.playActiveVideo();
    } else {
      this.isInView = false;
      this.pauseAllVideos();
    }
  }

  activate(type, { force = false } = {}) {
    if (!type) return;
    if (!force && type === this.activeType) return;

    this.activeType = type;

    this.toggleButtons.forEach((button) => {
      const isActive = button.dataset.toggleTarget === type;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.videoPanels.forEach((panel) => {
      const isActive = panel.dataset.videoType === type;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;

      const video = panel.querySelector('[data-video-element]');
      if (!video) return;

      if (!isActive) {
        video.pause();
      }
    });

    if (this.isInView) {
      this.playActiveVideo();
    }
  }

  playActiveVideo() {
    const activePanel = this.videoPanels.find((panel) => panel.dataset.videoType === this.activeType);
    const activeVideo = activePanel?.querySelector('[data-video-element]');

    if (!activeVideo) return;

    loadPdpHumidityToggleVideo(activeVideo);
    playPdpHumidityToggleVideo(activeVideo);
  }

  pauseAllVideos() {
    this.videoElements.forEach((video) => {
      video.pause();
    });
  }
}

const initPdpHumidityToggleSections = () => {
  const sections = document.querySelectorAll('.pdp-humidity-toggle');

  sections.forEach((section) => {
    if (section.dataset.pdpHumidityToggleInitialized === 'true') return;

    section.dataset.pdpHumidityToggleInitialized = 'true';
    new PdpHumidityToggle(section);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpHumidityToggleSections);
} else {
  initPdpHumidityToggleSections();
}
